export function describeEyewearOption(optionNames, optionValues) {
  const result = { frame: '', lens: '', size: '', lensTypes: [] };
  optionNames.forEach((name, index) => {
    const value = (optionValues[index] || '').trim();
    if (/size/i.test(name)) result.size = value;
    else if (/lens/i.test(name) && !/frame|colour and|color and/i.test(name)) result.lens = value;
    else if (/colou?r|frame/i.test(name)) {
      const combined = /frame and lens|colou?r and lens/i.test(name);
      const separator = combined ? /\s+\/\s+|\s+-\s+/ : /\s+-\s+/;
      const parts = value.split(separator);
      // Code-only colourways are not readable frame or lens names.
      if (/^[\dA-Z]+\s*\/\s*\d+$/i.test(value) && !/[a-z]{3}/i.test(value)) return;
      result.frame = parts.shift() || '';
      if (parts.length) result.lens = parts.join(' - ');
    }
  });
  if (/\bpolar(?:i[sz]ed)?\b/i.test(result.lens) && !/non.?polar|unpolar/i.test(result.lens)) result.lensTypes.push('Polarised');
  if (/\btransitions?\b|photochromic/i.test(result.lens)) result.lensTypes.push('Transitions');
  if (/\bprizm\b/i.test(result.lens)) result.lensTypes.push('Prizm');
  if (/clear demo/i.test(result.lens)) result.lensTypes.push('Demo lenses');
  else if (/\bclear\b/i.test(result.lens)) result.lensTypes.push('Clear');
  return result;
}

function displayName(value) {
  value = value.replace(/\s*-\s*polar$/i, ' / Polarised').replace(/\bpolarized\b/gi, 'Polarised');
  if (value !== value.toUpperCase()) return value;
  return value.toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase());
}

function priority(option) {
  const frame = option.frame.toLowerCase();
  const black = /black/.test(frame);
  const grey = /gr[ae]y/i.test(option.lens) && !/transitions/i.test(option.lens);
  return (black ? 0 : /brown|havana|tortoise/.test(frame) ? 10 : 20) + (grey ? 0 : 1);
}

export function eyewearDelivery({ available, tracked, inventory, supply, leadTime, supplierOrder, sunglasses, meta, backorderEstimate }) {
  if (available !== true) return { status: 'sold_out', text: 'This option is currently sold out.' };
  if (sunglasses && meta !== true) return { status: 'stock_confirmation', text: 'If not in stock, please allow 1-2 weeks for dispatch. To confirm stock availability, message us or call 0800 MR MONEY.' };
  if (tracked && Number(inventory) > 0) return { status: 'in_stock', text: sunglasses ? 'In stock - ships straight away on business days.' : 'In stock at Mr Money - same-day dispatch on business days.' };
  if (supply === 'merchant_preorder') return { status: supply, text: 'If not in stock, please allow 1-2 weeks for dispatch. To confirm stock availability, message us or call 0800 MR MONEY.' };
  if (supply === 'backorder' && backorderEstimate) return { status: supply, text: `Supplier backorder - estimated arrival in ${backorderEstimate}.` };
  if (supply === 'backorder') return { status: supply, text: sunglasses ? 'On backorder - please expect a 2-week delay.' : 'Backorder - estimated arrival in around 1 month.' };
  if (supply === 'incoming_order') return { status: supply, text: 'On incoming order - estimated arrival in 1-2 weeks.' };
  if (supply === 'supplier_available') return { status: supply, text: 'Supplier order - estimated arrival in 1-2 weeks.' };
  if (leadTime) return { status: 'supplier_estimate', text: 'Supplier order - estimated 1-2 weeks, subject to availability.' };
  if (supplierOrder) return { status: 'unknown', text: 'Supplier order - contact us to confirm arrival timing.' };
  return { status: 'unknown', text: 'Contact us to confirm availability and arrival timing.' };
}

const BrowserElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
class EyewearDelivery extends BrowserElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.variants = JSON.parse(this.querySelector('[data-delivery-variants]').textContent);
    this.select(this.dataset.selectedVariant);
    this.closest('.shopify-section, section')?.addEventListener('variant:update', event => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      this.select(event.detail.resource?.id);
      // Use the fresh storefront amount so country-specific pricing is respected.
      const selector = 'square-placement[data-platform="shopify-app-blocks"]';
      const fresh = event.detail.data.html?.querySelector(selector);
      const placement = this.closest('.shopify-section, section')?.querySelector(selector);
      if (fresh && placement) {
        for (const key of ['amount', 'currency']) {
          if (fresh.dataset[key]) placement.dataset[key] = fresh.dataset[key];
        }
      }
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  select(id) {
    const variant = this.variants.find(variant => String(variant.id) === String(id));
    if (!variant) return;
    this.dataset.selectedVariant = String(id);
    const delivery = eyewearDelivery({ ...variant, backorderEstimate: this.dataset.backorderEstimate, leadTime: this.dataset.leadTime === 'true', supplierOrder: this.dataset.supplierOrder === 'true', sunglasses: this.dataset.sunglasses === 'true', meta: this.dataset.meta === 'true' });
    const output = this.querySelector('[data-mm-sgh-arrival]');
    output.dataset.supplyStatus = delivery.status;
    output.textContent = delivery.text;
  }
}

class EyewearOptions extends BrowserElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.controller = new AbortController();
    const names = JSON.parse(this.querySelector('[data-option-names]').textContent);
    this.tiles = Array.from(this.querySelectorAll('[data-mm-sgh-tile]'));
    this.tiles.forEach(tile => {
      const values = [tile.dataset.optionOne, tile.dataset.optionTwo, tile.dataset.optionThree];
      const details = describeEyewearOption(names, values);
      tile.details = details;
      tile.group = JSON.stringify([details.frame, details.lens, names.filter(name => !/size/i.test(name)).map(name => values[names.indexOf(name)])]);
      tile.querySelector('[data-frame-label]').textContent = displayName(details.frame) || values.find(Boolean);
      tile.querySelector('[data-lens-label]').textContent = displayName(details.lens);
      tile.setAttribute('aria-label', [details.frame, details.lens, details.size].filter(Boolean).join(', '));
    });
    this.groups = new Map();
    this.tiles.forEach(tile => {
      if (!this.groups.has(tile.group)) this.groups.set(tile.group, []);
      this.groups.get(tile.group).push(tile);
    });
    const strip = this.querySelector('[data-mm-sgh-tiles]');
    [...this.groups.values()].sort((a, b) => priority(a[0].details) - priority(b[0].details)).forEach(group => {
      group.forEach((tile, index) => { tile.hidden = index > 0; strip.append(tile); });
    });
    this.querySelector('[data-mm-sgh-count]').textContent = `${this.groups.size} ${this.groups.size === 1 ? 'option' : 'options'}`;
    this.addEventListener('click', event => {
      const tile = event.target.closest('[data-mm-sgh-tile]');
      if (!tile || tile.disabled) return;
      const group = this.groups.get(tile.group);
      const target = group.find(item => item.details.size === this.selected?.details.size && item.dataset.available === 'true')
        || group.find(item => item.dataset.available === 'true') || tile;
      this.select(target);
    }, { signal: this.controller.signal });
    this.addEventListener('change', () => this.syncFromNative(), { signal: this.controller.signal });
    this.native = this.querySelector('[data-native-picker]');
    const selected = this.tiles.find(tile => tile.dataset.selected === 'true') || this.tiles[0];
    if (selected) this.render(selected);
    this.dataset.ready = 'true';
    this.native.hidden = true;
    this.querySelector('[data-eyewear-visual]').hidden = false;
    if (selected) this.reveal(selected, 'auto');
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.ready = false;
  }

  syncFromNative() {
    const values = Array.from(this.native.querySelectorAll('fieldset, select')).map(control =>
      control instanceof HTMLSelectElement ? control.value : control.querySelector('input:checked')?.value || ''
    );
    const tile = this.tiles.find(tile => [tile.dataset.optionOne, tile.dataset.optionTwo, tile.dataset.optionThree].filter(Boolean).every((value, index) => value === values[index]));
    if (tile) this.render(tile);
  }

  select(tile) {
    if (!tile || tile.dataset.available !== 'true') return;
    const picker = this.native.querySelector('variant-picker');
    const controls = Array.from(picker?.querySelectorAll('fieldset, select') || []);
    const values = [tile.dataset.optionOne, tile.dataset.optionTwo, tile.dataset.optionThree].filter(Boolean);
    let target;
    values.forEach((value, index) => {
      const control = controls[index];
      if (control instanceof HTMLSelectElement) {
        const option = Array.from(control.options).find(option => option.value === value);
        if (!option) return;
        control.value = value;
        Array.from(control.options).forEach(item => item.toggleAttribute('selected', item === option));
        target = control;
        option.dataset.variantId = tile.dataset.variantId;
      } else {
        const input = Array.from(control?.querySelectorAll('input') || []).find(input => input.value === value);
        if (!input) return;
        input.checked = true;
        input.dataset.variantId = tile.dataset.variantId;
        target = input;
      }
    });
    if (!target) return;
    this.render(tile);
    this.reveal(tile);
    target.dispatchEvent(new Event('change', { bubbles: true }));
    const url = new URL(location.href);
    url.searchParams.set('variant', tile.dataset.variantId);
    history.replaceState({}, '', url);
  }

  reveal(tile, behavior = 'smooth') {
    const representative = this.groups.get(tile.group)[0];
    const strip = this.querySelector('[data-mm-sgh-tiles]');
    const bounds = strip.getBoundingClientRect();
    const option = representative.getBoundingClientRect();
    const left = option.left < bounds.left ? option.left - bounds.left - 2
      : option.right > bounds.right ? option.right - bounds.right + 2 : 0;
    if (left) strip.scrollBy({ left, behavior });
  }

  render(tile) {
    this.selected = tile;
    const details = tile.details;
    this.groups.forEach(group => {
      const representative = group[0];
      const selected = tile.group === representative.group;
      representative.classList.toggle('is-selected', selected);
      representative.setAttribute('aria-pressed', String(selected));
      representative.disabled = group.every(item => item.dataset.available !== 'true');
      representative.querySelector('[data-option-stock]').textContent = representative.disabled ? 'Sold out' : '';
    });
    for (const [key, value] of Object.entries({ frame: details.frame, lens: details.lens, type: details.lensTypes.join(' + '), size: details.size })) {
      const output = this.querySelector(`[data-selected-${key}]`);
      output.textContent = displayName(value);
      output.parentElement.hidden = !value;
    }
    const help = this.querySelector('[data-lens-help]');
    help.textContent = details.lensTypes.includes('Transitions') ? 'Transitions lenses adapt to changing light.'
      : details.lensTypes.includes('Polarised') ? 'Polarised lenses reduce reflected glare.'
      : details.lensTypes.includes('Demo lenses') ? 'Demo lenses are not prescription lenses. Ask us about fitting your prescription.' : '';
    help.hidden = !help.textContent;
    const sizeWrap = this.querySelector('[data-mm-sgh-sizes]');
    sizeWrap.replaceChildren();
    const sizes = this.groups.get(tile.group);
    sizeWrap.hidden = sizes.length < 2;
    if (sizes.length > 1) {
      const label = document.createElement('span');
      label.textContent = 'Size';
      sizeWrap.append(label);
      sizes.forEach(size => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mm-sgh-selector__size';
        button.textContent = size.details.size;
        button.disabled = size.dataset.available !== 'true';
        button.setAttribute('aria-pressed', String(size === tile));
        button.classList.toggle('is-selected', size === tile);
        button.addEventListener('click', () => this.select(size));
        sizeWrap.append(button);
      });
    }
    this.closest('.shopify-section, section')?.querySelectorAll('mm-eyewear-delivery').forEach(delivery => {
      if (delivery.dataset.productId === this.dataset.productId) delivery.select(tile.dataset.variantId);
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('mm-eyewear-delivery')) customElements.define('mm-eyewear-delivery', EyewearDelivery);
if (typeof customElements !== 'undefined' && !customElements.get('mm-eyewear-options')) customElements.define('mm-eyewear-options', EyewearOptions);



